import type { Config, Content } from '@ml/config';
import { Mulberry32 } from '../rng.ts';
import type { Hero } from '../resolve/classify.ts';
import { pickDistinct } from '../resolve/fx.ts';
import { createBattle, stepBattle } from './battle.ts';
import type { BattleAction, BattleEvent, BattleState, Buff, MonsterDef } from './types.ts';

export type RunContent = Pick<Content, 'monsters' | 'dungeons' | 'buffPool'>;

export type RunPhase = 'battle' | 'chooseBuff' | 'won' | 'lost';

export type RunStats = {
  readonly rounds: number;
  readonly maxDamage: number;
  readonly boostersCreated: number;
};

/**
 * Một lượt chạy dungeon (combat.md 7–8): các tầng nối nhau, HP mang qua tầng, hồi giữa tầng,
 * chọn buff rogue-lite. JSON thuần; RNG của lượt chạy (`rng`) tách khỏi RNG của từng trận.
 */
export type RunState = {
  readonly dungeonId: string;
  /** Tầng đang đánh / vừa thắng (0-based). */
  readonly floor: number;
  readonly floorCount: number;
  readonly baseHero: Hero;
  readonly buffs: readonly Buff[];
  readonly phase: RunPhase;
  readonly battle: BattleState;
  /** Buff đang mời chọn (phase `chooseBuff`). */
  readonly offer: readonly Buff[];
  readonly rng: number;
  readonly stats: RunStats;
};

export type RunAction = BattleAction | { readonly type: 'chooseBuff'; readonly index: number };

export type RunEvent =
  | { readonly type: 'invalid'; readonly reason: 'wrongPhase' | 'badIndex' }
  | { readonly type: 'battle'; readonly floor: number; readonly events: readonly BattleEvent[] }
  | { readonly type: 'floorWon'; readonly floor: number }
  | { readonly type: 'heal'; readonly amount: number; readonly heroHp: number }
  | { readonly type: 'buffOffer'; readonly buffs: readonly string[] }
  | { readonly type: 'buffChosen'; readonly buff: string }
  | { readonly type: 'floorStart'; readonly floor: number }
  | { readonly type: 'runWon' }
  | { readonly type: 'runLost' };

export type RunStepResult = { readonly state: RunState; readonly events: readonly RunEvent[] };

const pctOf = (base: number, pct: number) => Math.floor((base * (100 + pct)) / 100);

/** Chỉ số hero sau buff `statPct` (combat.md 2.1: `final = floor(base × (100 + Σpct) / 100)`). */
export function buffedHero(base: Hero, buffs: readonly Buff[]): Hero {
  const sum = (stat: string) =>
    buffs.reduce((s, b) => (b.type === 'statPct' && b.stat === stat ? s + b.value : s), 0);
  return {
    ...base,
    stats: {
      ...base.stats,
      hp: pctOf(base.stats.hp, sum('hp')),
      handAttack: pctOf(base.stats.handAttack, sum('handAttack')),
      abilityAttack: pctOf(base.stats.abilityAttack, sum('abilityAttack')),
    },
  };
}

/** Config trận sau buff `colorSpawnWeight` (+trọng số sinh một màu). */
export function buffedConfig(config: Config, buffs: readonly Buff[]): Config {
  const extra = buffs.filter((b) => b.type === 'colorSpawnWeight' && b.color);
  if (extra.length === 0) return config;
  const colorWeights = { ...config.board.spawn.colorWeights };
  for (const b of extra) colorWeights[b.color!] = (colorWeights[b.color!] ?? 0) + b.value;
  return {
    ...config,
    board: { ...config.board, spawn: { ...config.board.spawn, colorWeights } },
  };
}

export function ultGainPctOf(buffs: readonly Buff[]): number {
  return buffs.reduce((s, b) => (b.type === 'ultGainPct' ? s + b.value : s), 0);
}

function floorWaves(run: Pick<RunState, 'dungeonId'>, floor: number, content: RunContent) {
  const dungeon = content.dungeons.find((d) => d.id === run.dungeonId)!;
  const byId = new Map(content.monsters.map((m) => [m.id, m]));
  return dungeon.floors[floor]!.waves.map((w) =>
    w.monsters.map((id) => {
      const m = byId.get(id);
      if (!m) throw new RangeError(`monster không có trong content: ${id}`);
      return m;
    }),
  ) as MonsterDef[][];
}

function startFloor(
  base: Omit<RunState, 'battle' | 'phase' | 'offer'>,
  heroHp: number | undefined,
  rng: Mulberry32,
  config: Config,
  content: RunContent,
): RunState {
  const hero = buffedHero(base.baseHero, base.buffs);
  const battle = createBattle({
    hero,
    waves: floorWaves(base, base.floor, content),
    seed: rng.nextU32(),
    config: buffedConfig(config, base.buffs),
    heroHp,
    ultGainPct: ultGainPctOf(base.buffs),
  });
  return { ...base, phase: 'battle', battle, offer: [], rng: rng.state };
}

export type CreateRunInput = {
  readonly hero: Hero;
  readonly dungeonId: string;
  readonly seed: number;
  readonly config: Config;
  readonly content: RunContent;
};

/** Bắt đầu lượt chạy: tầng 1, không buff. Số tầng = min(số tầng dungeon, `dungeon.floorsPerRun`). */
export function createRun(input: CreateRunInput): RunState {
  const dungeon = input.content.dungeons.find((d) => d.id === input.dungeonId);
  if (!dungeon) throw new RangeError(`dungeon không có trong content: ${input.dungeonId}`);
  const rng = new Mulberry32(input.seed);
  return startFloor(
    {
      dungeonId: dungeon.id,
      floor: 0,
      floorCount: Math.min(dungeon.floors.length, input.config.dungeon.floorsPerRun),
      baseHero: input.hero,
      buffs: [],
      rng: rng.state,
      stats: { rounds: 0, maxDamage: 0, boostersCreated: 0 },
    },
    undefined,
    rng,
    input.config,
    input.content,
  );
}

function updateStats(
  stats: RunStats,
  events: readonly BattleEvent[],
  roundDone: boolean,
): RunStats {
  let { rounds, maxDamage, boostersCreated } = stats;
  for (const e of events) {
    if (e.type !== 'player') continue;
    maxDamage = Math.max(maxDamage, e.total);
    boostersCreated += e.events.filter((x) => x.type === 'boosterCreated').length;
  }
  if (roundDone) rounds++;
  return { rounds, maxDamage, boostersCreated };
}

/**
 * Một bước lượt chạy. Phase `battle`: chuyển `release` cho `stepBattle` (config đã áp buff);
 * thắng tầng → hồi `dungeon.healBetweenFloorsPct` → mời `dungeon.buffChoiceCount` buff rút đều
 * bằng RNG lượt chạy (content-mvp: M1 không theo độ hiếm). Phase `chooseBuff`: nhận buff, vào tầng kế.
 */
export function stepRun(
  run: RunState,
  action: RunAction,
  config: Config,
  content: RunContent,
): RunStepResult {
  if (action.type === 'chooseBuff') return chooseBuff(run, action.index, config, content);
  if (run.phase !== 'battle')
    return { state: run, events: [{ type: 'invalid', reason: 'wrongPhase' }] };
  const res = stepBattle(run.battle, action, buffedConfig(config, run.buffs));
  if (res.state === run.battle) {
    return { state: run, events: [{ type: 'battle', floor: run.floor, events: res.events }] };
  }
  const battle = res.state;
  const events: RunEvent[] = [{ type: 'battle', floor: run.floor, events: res.events }];
  const roundDone = battle.round !== run.battle.round || battle.status !== 'ongoing';
  const stats = updateStats(run.stats, res.events, roundDone);
  if (battle.status === 'lost') {
    events.push({ type: 'runLost' });
    return { state: { ...run, battle, stats, phase: 'lost' }, events };
  }
  if (battle.status === 'ongoing') return { state: { ...run, battle, stats }, events };
  events.push({ type: 'floorWon', floor: run.floor });
  if (run.floor + 1 >= run.floorCount) {
    events.push({ type: 'runWon' });
    return { state: { ...run, battle, stats, phase: 'won' }, events };
  }
  const hs = battle.heroState;
  const amount = Math.floor((hs.maxHp * config.dungeon.healBetweenFloorsPct) / 100);
  const heroHp = Math.min(hs.maxHp, hs.hp + amount);
  events.push({ type: 'heal', amount, heroHp });
  const rng = new Mulberry32(run.rng);
  const offer = pickDistinct(rng, content.buffPool, config.dungeon.buffChoiceCount);
  events.push({ type: 'buffOffer', buffs: offer.map((b) => b.id) });
  return {
    state: {
      ...run,
      battle: { ...battle, heroState: { ...hs, hp: heroHp } },
      stats,
      phase: 'chooseBuff',
      offer,
      rng: rng.state,
    },
    events,
  };
}

function chooseBuff(
  run: RunState,
  index: number,
  config: Config,
  content: RunContent,
): RunStepResult {
  if (run.phase !== 'chooseBuff') {
    return { state: run, events: [{ type: 'invalid', reason: 'wrongPhase' }] };
  }
  const buff = run.offer[index];
  if (!buff) return { state: run, events: [{ type: 'invalid', reason: 'badIndex' }] };
  const buffs = [...run.buffs, buff];
  // Buff HP tăng HP tối đa thì HP hiện tại tăng đúng phần chênh.
  const prevMax = buffedHero(run.baseHero, run.buffs).stats.hp;
  const newMax = buffedHero(run.baseHero, buffs).stats.hp;
  const heroHp = run.battle.heroState.hp + (newMax - prevMax);
  const rng = new Mulberry32(run.rng);
  const next = startFloor({ ...run, floor: run.floor + 1, buffs }, heroHp, rng, config, content);
  return {
    state: next,
    events: [
      { type: 'buffChosen', buff: buff.id },
      { type: 'floorStart', floor: next.floor },
    ],
  };
}
